import type { ReconnectFailReason } from "@bn-games/network-protocol";
import type { RoomClient } from "./RoomClient.js";

/**
 * Matches backgammon's existing `handleOnlineResume` — long enough to hear
 * back from a live server, short enough that a genuinely dead one doesn't
 * strand the player behind a silent "reconnecting…" modal. Only covers the
 * pre-response window; a reply of any kind (including "seated, room not
 * full") cancels it.
 */
const RESUME_TIMEOUT_MS = 10_000;

export type ResumeFailureCause = "SEAT_TAKEN" | "ROOM_GONE" | "ROOM_FULL" | "UNREACHABLE";

export type ResumeOutcome =
  | { readonly kind: "ready" }
  | { readonly kind: "waiting" }
  | { readonly kind: "failed"; readonly cause: ResumeFailureCause };

export interface ResumeOptions {
  readonly roomCode: string;
  readonly playerName: string;
  /** Pre-response timeout in ms. Defaults to `RESUME_TIMEOUT_MS`. */
  readonly timeoutMs?: number;
  /**
   * Fired once when the server seats this client as a new player and the
   * room isn't full yet (`RESUME_ROOM` answered `ROOM_JOINED`, not
   * `RECONNECT_OK`) — the caller's cue to navigate to a waiting surface. For
   * a 2-seat room `GAME_START` follows in the same tick, so this is a
   * sub-frame transient; for a 3/4-seat room it can be a genuine
   * multi-minute wait, which is why the pre-response timeout is cancelled
   * the moment this fires.
   *
   * Receives an `abandon` callback: call it if the player leaves the waiting
   * surface before a later `ready`/`failed` signal arrives (e.g. backs out
   * of the room). Without it the promise is left pending forever and this
   * call's `onUpdate`/`onReconnectFail` listeners stay subscribed to
   * `roomClient` for the rest of its lifetime — including reacting to
   * whatever unrelated room that same long-lived `RoomClient` is used for
   * next. `abandon` only ever fires on the caller's explicit say-so; it is
   * never inferred from `roomClient`'s own state machine, which legitimately
   * passes through `"idle"` mid-sequence on unrelated failure paths too
   * (e.g. `RECONNECT_FAIL` handling) — inferring abandonment from that would
   * misfire there.
   */
  readonly onSeatedWaiting?: (abandon: () => void) => void;
}

function mapReconnectFailReason(reason: ReconnectFailReason): ResumeFailureCause {
  switch (reason) {
    case "SEAT_TAKEN":
      return "SEAT_TAKEN";
    case "ROOM_NOT_FOUND":
      return "ROOM_GONE";
    case "EXPIRED":
    case "INVALID_TOKEN":
      // Unreachable on the RESUME_ROOM path in practice — mapped
      // defensively rather than asserting the invariant.
      return "UNREACHABLE";
    default:
      return "UNREACHABLE";
  }
}

/**
 * Wraps `RoomClient.resume()` — fire-and-forget on its own — into a single
 * `Promise<ResumeOutcome>`, reassembling the result from `onUpdate` +
 * `onReconnectFail` + a pre-response timeout so every consumer doesn't have
 * to hand-roll the same race. The pattern this was lifted from is
 * backgammon's `handleOnlineResume` (`apps/backgammon/src/main.ts`).
 */
export function resumeRoom<TClient, TServer>(
  roomClient: RoomClient<TClient, TServer>,
  options: ResumeOptions,
): Promise<ResumeOutcome> {
  const timeoutMs = options.timeoutMs ?? RESUME_TIMEOUT_MS;

  return new Promise<ResumeOutcome>((resolve) => {
    let settled = false;
    let seatedWaitingFired = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const finish = (outcome: ResumeOutcome): void => {
      if (settled) return;
      settled = true;
      clearTimer();
      unsubUpdate();
      unsubFail();
      resolve(outcome);
    };

    // Only ever invoked by the caller (see `onSeatedWaiting`'s doc comment)
    // — never inferred from `roomClient`'s own state machine. Unlike
    // `finish()`, this never resolves: the caller already gave up on this
    // attempt, so there is no outcome left for it to act on, and settling
    // with `{ kind: "failed" }` would risk popping a failure modal over
    // whatever the app does with this `RoomClient` next.
    const abandon = (): void => {
      if (settled) return;
      settled = true;
      clearTimer();
      unsubUpdate();
      unsubFail();
    };

    const fail = (cause: ResumeFailureCause): void => {
      if (settled) return;
      // Tear down the in-flight resume so a late RECONNECT_OK/ROOM_JOINED
      // can't start a game behind an already-reported failure — the same
      // teardown backgammon's `failAndCleanup` does today.
      roomClient.cancel();
      roomClient.getConnection().disconnect();
      finish({ kind: "failed", cause });
    };

    timer = setTimeout(() => fail("UNREACHABLE"), timeoutMs);

    const unsubUpdate = roomClient.onUpdate((info) => {
      if (settled) return;
      if (info.state === "ready") {
        finish({ kind: "ready" });
        return;
      }
      if (info.state === "joining" && info.localSeat !== null) {
        // Seated as a new player; the room isn't full yet. A 4-seat room can
        // legitimately wait minutes for GAME_START, so the pre-response
        // timeout no longer applies — the promise stays pending until a
        // later "ready" or "failed" signal arrives.
        clearTimer();
        if (!seatedWaitingFired) {
          seatedWaitingFired = true;
          options.onSeatedWaiting?.(abandon);
        }
        return;
      }
      if (info.state === "error") {
        fail(info.errorCode === "ROOM_FULL" ? "ROOM_FULL" : "UNREACHABLE");
      }
    });

    const unsubFail = roomClient.onReconnectFail((reason) => {
      fail(mapReconnectFailReason(reason));
    });

    roomClient.resume(options.roomCode, options.playerName);
  });
}
